/**
 * Standalone validation rules for Vuetify :rules prop.
 *
 * Each rule is a function that takes a value and returns true or an error string.
 * Can be imported individually or used via the rules mixin (backward compatible).
 *
 * Usage:
 *   import { required, email, integer } from '@/utils/validation'
 *   <v-text-field :rules="[required, email]" />
 *
 * Composition:
 *   import { composeRules, required, email } from '@/utils/validation'
 *   <v-text-field :rules="[composeRules(required, email)]" />
 */

// --- Basic presence ---

export const required = v => !!(v) || 'Required'

export const requiredNoSpaces = v => (v && v.trim() !== '') || 'Required'

export const requiredCheckbox = v => !!(v === true || v === false) || 'Item is required'

// --- String format ---

export const username = v => /^\w*$/.test(v) || 'Username is not valid'

export const email = v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || 'Email is not valid'

// The backend's teammate-invite address rule (``_PROFILE_EMAIL_REGEX`` in
// api/src/endpoints/distributor.py). Stricter than ``email`` above: no
// apostrophes, a 2+ letter TLD. Use it where the backend enforces that rule,
// so the form refuses what the API would refuse.
export const inviteEmail = v =>
  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(v) || 'Email is not valid'

export const noSpecialChars = v => /^[a-zA-Z0-9 ]*$/.test(v) || 'Special symbols are not allowed'

export const domain = v =>
  !v ||
  /(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]/.test(
    v.toString().trim()
  ) ||
  'Domain format is incorrect'

// Shopper store URL slug. Must be lowercase and start with a LETTER so
// the storefront entry resolver can treat a digit-leading segment as a
// numeric store ID unambiguously (#1888). Mirrors the backend pattern
// ^[a-z][a-z0-9-]*$ (Store.slug / shopper_url validator). No
// leading/trailing hyphen.
export const url = v =>
  !v ||
  /^[a-z]([a-z0-9-]*[a-z0-9])?$/.test(v.toString().trim()) ||
  'URL format is incorrect'

export const zipCode = v => !v || /^[0-9-]*$/.test(v) || 'Zip code is incorrect'

// --- Numeric ---

export const number = v =>
  (v && /^[0-9]*\.{0,1}[0-9]*$/.test(v.toString().trim())) || 'Number is incorrect'

export const integer = v => /^[0-9]*$/.test(v) || 'Number must be an integer'

export const supplierCost = v =>
  /^\d+(?:\.\d{0,2})?$/.test(v) || v === 0 || 'Number is incorrect'

export const marginNumber = v =>
  (v && /^-?\d*(\.\d+)?$/.test(v.toString().trim())) || 'Number is incorrect'

export const allowance = v =>
  /^\d+(?:\.\d{0,2})?$/.test(v) || v === 0 || 'Please enter a dollar amount'

export const decoration = v => /^\d+(?:\.\d{0,2})?$/.test(v) || 'Number is incorrect'

export const percent = v => (v > 0 && v <= 100) || 'Percent has to be between 0 and 100'

export const greaterThanZero = v =>
  (v && parseFloat(v) > 0) || 'Must be greater than 0'

// --- Parameterized (return a rule function) ---

export const maxValue = limit => v =>
  (typeof v !== 'undefined' && Number(v) <= limit) || `Can be max ${limit}`

export const minValue = limit => v =>
  (typeof v !== 'undefined' && Number(v) >= limit) || `Has to be at least ${limit}`

export const maxLength = limit => v => (v || '').length <= limit || 'Out of limit'

export const minLength = limit => v =>
  (v || '').length >= limit || `Please select at least ${limit}`

export const maxIntValue = limit => v =>
  (parseInt(v) || 0) <= limit || 'Out of limit'

export const oneOf = valuesArr => v =>
  (valuesArr && Array.isArray(valuesArr) && valuesArr.find(va => va)) ||
  'One of these have to be selected'

// --- Composition ---

/**
 * Compose multiple rules into a single rule function.
 * Returns true if all pass, or the first error string encountered.
 *
 * Usage:
 *   composeRules(required, email)
 *   // Returns a single function: v => true | 'error string'
 */
export const composeRules = (...rules) => v => {
  for (const rule of rules) {
    const result = rule(v)
    if (result !== true) return result
  }
  return true
}
